import { Platform } from 'react-native';
import * as Sharing from 'expo-sharing';
// Capture is loaded on demand; see lazyCapture.js for why both dependencies share one
// dynamic-import boundary rather than getting one each.
import { captureRef, releaseCapture, loadHtml2Canvas } from '@/constants/utils/lazyCapture';

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// html2canvas rasterizes inline SVGs by drawing them as images, which taints
// the canvas in some browsers (SecurityError on export). Swap every svg in the
// clone for a colored disc before rendering.
const replaceSvgsWithDiscs = (clonedDoc, accentColor) => {
  clonedDoc.querySelectorAll('[data-card-capture] svg').forEach(svg => {
    let fill = '';
    try {
      fill = clonedDoc.defaultView?.getComputedStyle(svg)?.color || '';
    } catch (_) {}
    fill = fill || svg.querySelector('path')?.getAttribute('fill') || svg.getAttribute('fill') || '';
    const size = Number.parseFloat(svg.getAttribute('width')) || 13;
    const marker = clonedDoc.createElement('span');
    const disc = Math.max(6, Math.round(Math.min(size, 14) * 0.66));
    marker.style.display = 'inline-block';
    marker.style.width = `${disc}px`;
    marker.style.height = `${disc}px`;
    marker.style.borderRadius = '999px';
    marker.style.background = (!fill || fill === 'currentColor') ? accentColor : fill;
    svg.replaceWith(marker);
  });
};

const captureWeb = async (node, accentColor) => {
  const html2canvas = await loadHtml2Canvas();
  const canvas = await html2canvas(node, {
    backgroundColor: null,
    scale: Math.min(2, window.devicePixelRatio || 1),
    logging: false,
    onclone: clonedDoc => replaceSvgsWithDiscs(clonedDoc, accentColor),
  });
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob(
        result => (result ? resolve(result) : reject(new Error('Could not create card image'))),
        'image/png',
      );
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Captures a card DOM node / native view as a PNG and hands it to the user.
 * Web: download anchor (or the Web Share sheet on mobile browsers).
 * Native: the OS share dialog. Throws on failure.
 */
export async function exportCardImage(node, title, accentColor) {
  if (!node) throw new Error('Card is not ready to save');

  const safeName = (title || 'card')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60) || 'card';

  if (Platform.OS === 'web') {
    const blob = await captureWeb(node, accentColor);
    const filename = `${safeName}.png`;
    const file = typeof File === 'function' ? new File([blob], filename, { type: 'image/png' }) : null;
    const isMobileBrowser = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '')
      || ((navigator.maxTouchPoints || 0) > 1 && window.innerWidth < 768);
    if (isMobileBrowser && file && typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: title || 'Card' });
      } catch (shareError) {
        if (shareError?.name !== 'AbortError') downloadBlob(blob, filename);
      }
    } else {
      downloadBlob(blob, filename);
    }
    return;
  }

  // iOS: drawViewHierarchyInRect often succeeds with a blank image for tall
  // cards (recipes/workouts inside chat). renderInContext is slower but
  // reliably captures large view trees. Also requires a real native view —
  // capture targets must set collapsable={false}.
  const uri = await captureRef(node, {
    format: 'png',
    quality: 1,
    result: 'tmpfile',
    ...(Platform.OS === 'ios' ? { useRenderInContext: true } : {}),
  });
  try {
    if (!(await Sharing.isAvailableAsync())) throw new Error('Sharing is not available');
    await Sharing.shareAsync(uri, {
      mimeType: 'image/png',
      dialogTitle: `Save ${title || 'card'}`,
      suggestedFilename: `${safeName}.png`,
      UTI: 'public.png',
    });
  } finally { if (Platform.OS === 'macos') await releaseCapture(uri); }
}
